import { fg } from '@atlaskit/platform-feature-flags/fg';

import { NLPSearchErrorState } from '../../../ai-answer-dialog/nlp-search-error-state';
import { NLPSearchResultFormat } from '../../../ai-answer-dialog/nlp-search-result-format';
import type { SearchAIAnswerQueryType, TraceHeaders } from '../../../ai-answer-dialog/types';
import { createAtlAttributionHeader } from '../../../common/fetch/atl-attribution-header';
import { fetchAggResponse } from '../../../common/fetch/fetch-agg-response';
import { logger } from '../../../common/utils/logger';
import {
	type AgentStudioGetAgentsResponse,
	KNOWLEDGE_CARD_AGENT_SEARCH_OPERATION_NAME,
	KnowledgeCardAgentSearchQuery,
} from './gql';

type fetchAgentCardProps = {
	query: string;
	sourceProduct: string;
	limit: number;
	cloudId: string;
	workspaceId: string;
	traceHeaders?: TraceHeaders;
};

const getAgentSearchInput = (query: string): string => {
	// Extract agent name from query if it's in a specific format
	// For now, use the query as-is, but this could be enhanced to parse specific formats
	return query.trim();
};

const getHttpStatusCode = (error: unknown): number | undefined => {
	if (!error || typeof error !== 'object' || !('code' in error)) {
		return undefined;
	}
	const code = error.code;
	return typeof code === 'number' && Number.isInteger(code) && code >= 100 && code <= 599
		? code
		: undefined;
};

const getSafeGraphQLErrorAttribute = (value: string | null | undefined): string | undefined =>
	value && /^[a-zA-Z0-9_.-]{1,64}$/.test(value) ? value : undefined;

export const fetchAgentCardData = ({
	query,
	limit,
	sourceProduct,
	cloudId,
	workspaceId,
	traceHeaders,
}: fetchAgentCardProps): Promise<SearchAIAnswerQueryType> => {
	let responseTraceId: string | undefined;
	let responseRequestId: string | undefined;
	let responseError: Error | undefined;
	let responseReceived = false;
	let graphqlErrorCount = 0;
	let graphqlErrorClassification: string | undefined;
	let graphqlErrorSource: string | undefined;
	const agentSearchInput = getAgentSearchInput(query);

	return fetchAggResponse<
		{ cloudId: string; input: { name: string }; limit: number },
		AgentStudioGetAgentsResponse
	>({
		graphQuery: KnowledgeCardAgentSearchQuery,
		variables: {
			cloudId,
			input: { name: agentSearchInput },
			limit,
		},
		operationName: KNOWLEDGE_CARD_AGENT_SEARCH_OPERATION_NAME,
		headers: {
			...createAtlAttributionHeader({
				atlWorkspaceId: workspaceId,
				tenantId: cloudId,
				product: sourceProduct ?? '',
			}),
			...(traceHeaders ?? undefined),
		},
	})
		.then((response) => {
			responseReceived = true;
			responseTraceId =
				response.gatewayTracingHeaders?.['x-trace-id'] ??
				response.extensions?.gateway?.trace_id ??
				undefined;
			responseRequestId =
				response.gatewayTracingHeaders?.['atl-request-id'] ??
				response.extensions?.gateway?.request_id ??
				undefined;
			if (response.errors) {
				graphqlErrorCount = response.errors.length;
				graphqlErrorClassification = getSafeGraphQLErrorAttribute(
					response.errors[0]?.extensions?.classification,
				);
				graphqlErrorSource = getSafeGraphQLErrorAttribute(
					response.errors[0]?.extensions?.errorSource,
				);
				responseError = new Error('Agent card GraphQL response contained errors');
				throw responseError;
			}

			const edges = response.data?.agentStudio_getAgents?.edges || [];
			const agents = edges.map((edge) => {
				const agent = edge.node;
				return {
					...agent,
					// Normalize agent ID by splitting on "/" and taking the last item
					id: agent.id.split('/').pop(),
					// Replace creator object with just the creator ID
					creator: agent.creator?.id,
					creator_type: agent.creatorType,
					identity_account_id: agent.identityAccountId,
				};
			});

			return {
				nlpSearch: {
					nlpResult: JSON.stringify(agents),
					uniqueSources: [],
					disclaimer: null,
					errorState: null,
					format: NLPSearchResultFormat.JSON,
					...(fg('kd_fix_sain_mau_instrumentation')
						? {
								extraAPIAnalyticsAttributes: {
									requestId: response.gatewayTracingHeaders?.['atl-request-id'] ?? null,
									traceId: response.gatewayTracingHeaders?.['x-trace-id'] ?? null,
								},
							}
						: {}),
				},
			};
		})
		.catch((error) => {
			const httpStatusCode = getHttpStatusCode(error);
			const isGraphQLError = responseError !== undefined && error === responseError;
			let failureReason = 'fetch_error';
			if (isGraphQLError) {
				failureReason = 'graphql_error';
			} else if (responseReceived) {
				failureReason = 'response_mapping_error';
			} else if (httpStatusCode) {
				failureReason = 'http_error';
			} else if (error instanceof SyntaxError) {
				failureReason = 'response_parse_error';
			}
			let category: 'graphql' | 'parse' | 'network' = 'network';
			if (isGraphQLError) {
				category = 'graphql';
			} else if (
				failureReason === 'response_mapping_error' ||
				failureReason === 'response_parse_error'
			) {
				category = 'parse';
			}
			const traceId = responseTraceId ?? traceHeaders?.['X-B3-TraceId'];
			if (fg('kd_search_ai_robust_sentry_logs') || fg('kd_enable_networkerror_logging')) {
				logger.error({
					feature: 'agent-knowledge-card',
					element: 'container',
					operation: 'fetch',
					category,
					message: 'Knowledge card request failed',
					product: sourceProduct,
					traceId,
					requestId: responseRequestId,
					...(httpStatusCode && { errorCode: httpStatusCode }),
					context: {
						operationName: KNOWLEDGE_CARD_AGENT_SEARCH_OPERATION_NAME,
						failureReason,
						graphqlErrorCount,
						graphqlErrorClassification,
						graphqlErrorSource,
					},
					error: isGraphQLError ? undefined : error,
					legacyError: error,
					functionName: 'fetchAgentCardData',
				});
			}
			return Promise.resolve({
				nlpSearch: {
					errorState: NLPSearchErrorState.NetworkError,
					...(httpStatusCode && { errorCode: httpStatusCode }),
					networkErrorReason: failureReason,
					...(graphqlErrorClassification && {
						networkErrorDetails: graphqlErrorClassification,
					}),
					...(traceId && { networkErrorTraceId: traceId }),
					...(responseRequestId && {
						extraAPIAnalyticsAttributes: { requestId: responseRequestId },
					}),
					disclaimer: null,
					format: NLPSearchResultFormat.JSON,
					uniqueSources: [],
				},
			});
		});
};
